import { useMemo } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import type { Espelho, Ocorrencia } from "@/api/escala.service";
import { ocorrenciasPorRegra, type ContextoSemHorario } from "@/components/escala/espelho";
import { capitalizar } from "@/utils/format";

interface EscalaOcorrenciasModalProps {
  open: boolean;
  ocorrencias: Ocorrencia[];
  colaboradores: Espelho["colaboradores"];
  // Com ele, o dia sem horário diz o motivo de cada dia e como resolver.
  contexto?: ContextoSemHorario;
  onClose: () => void;
}

export function EscalaOcorrenciasModal({ open, ocorrencias, colaboradores, contexto, onClose }: EscalaOcorrenciasModalProps) {
  // A janela fica montada fechada, e o espelho redesenha a cada edição.
  const grupos = useMemo(() => ocorrenciasPorRegra(ocorrencias, colaboradores, contexto), [ocorrencias, colaboradores, contexto]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>Escala com problemas</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Alert severity="error">
            A escala tem ocorrências que impedem a validação. Corrija as células com contorno âmbar na grade e valide de novo.
          </Alert>

          {grupos.map((grupo) => (
            <Box key={grupo.regra}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {capitalizar(grupo.rotulo)}
              </Typography>
              {grupo.dica && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {grupo.dica}
                </Typography>
              )}
              <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, m: 0 }}>
                {grupo.colaboradores.flatMap((c, i) =>
                  (c.motivos ?? [{ periodos: c.periodos, motivo: undefined }]).map((m, j) => (
                    <Typography component="li" variant="body2" key={`${c.colaboradorId}-${i}-${j}`}>
                      <strong>{c.nome}</strong> — {m.periodos.join(", ")}
                      {m.motivo && `: ${m.motivo}`}
                    </Typography>
                  )),
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
